import * as THREE from "three";
import debug from "debug";
const log = debug("app:glb");

/**
 * Class responsible for deleting or clearing the scene while preserving certain elements, such as a grid.
 * @category Experience
 * @subcategory ObjectManagement
 */

class DeleteScene {
  /**
   * Constructs a DeleteScene instance.
   * @param {Object} experience - The main application experience object.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.selectedObjects = [];
    this.scene = this.experience.scene;
    this._onDeleteScene = (payload) => this.removeAllExceptGrid(payload);
    this.init();
  }

  /**
   * Initializes event listeners for scene deletion actions.
   */
  init() {
    this.eventHub.on("deleteScene", this._onDeleteScene);
    this.eventHub.on("clearScene", this.showClearSceneConfirmation.bind(this));
  }

  cleanup() {
    console.log("Cleaning up DeleteScene...");

    // Remove event listeners
    this.eventHub.off("deleteScene", this._onDeleteScene);
    this.eventHub.off("clearScene", this.showClearSceneConfirmation);

    // Clear stored references
    this.scene = null;
    this.eventHub = null;
    this.experience = null;
    this.selectedObjects = null;
    this.itemsInScene = null;

    console.log("DeleteScene cleanup complete.");
}

  /**
   * Displays a modal confirmation dialog for clearing the scene.
   */
  showClearSceneConfirmation() {
    this.eventHub.emit("modalInfo", {
      title: "Delete Scene",
      message:
        "You are about to delete the current scene. If you haven't saved your project, you will lose any unsaved changes. Do you want to continue?",
      actions: [
        { label: "Cancel", callback: this.cancelCallback.bind(this) },
        { label: "Continue", callback: this.continueCallback.bind(this) },
      ],
      autoClose: false,
    });
  }

  /**
   * Callback executed when the user cancels the scene deletion process.
   */
  cancelCallback() {
    log("Scene deletion canceled.");
  }

  /**
   * Callback executed when the user confirms the scene deletion process.
   */
  continueCallback() {
    log("Scene deletion confirmed.");
    this.removeAllExceptGrid();
  }

  /**
   * Removes all objects from the scene except the grid.
   * @param {Object} [options] - Optional payload from deleteScene event. If options.reason === "mode-switch", logEvent is not emitted (so 2D/3D switch does not create an undo slot).
   */
  removeAllExceptGrid(options) {
    const gridIdentifier = "myGrid";
    const objectsToRemove = [];
    const sceneSnapshot = this.experience.xmdVault;

    // Traverse the scene to find all objects except the grid
    this.scene.traverse((object) => {
      if (object.name !== gridIdentifier) {
        objectsToRemove.push(object);
      }
    });

    this.itemsInScene = [];

    // Remove the identified objects from the scene
    objectsToRemove.forEach((object) => {
      // Remove CSS3DObject DOM elements to prevent memory leak
      if (object.name === "CSS3DObject" && object.element) {
        if (object.element.parentNode) {
          object.element.parentNode.removeChild(object.element);
        }
      }
      // Traverse children for nested CSS3DObjects (e.g. inside groups)
      if (object.traverse) {
        object.traverse((child) => {
          if (child.name === "CSS3DObject" && child.element && child.element.parentNode) {
            child.element.parentNode.removeChild(child.element);
          }
        });
      }
      // If the object is part of a parent, remove it from the parent
      if (object.parent) {
        object.parent.remove(object);
      } else {
        // Otherwise, directly remove it from the scene
        this.scene.remove(object);
      }
      // Dispose geometry and materials (texture maps from TextureVault are shared, so we don't dispose those)
      if (object.geometry) {
        object.geometry.dispose();
      }
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((mat) => mat.dispose && mat.dispose());
        } else if (object.material.dispose) {
          object.material.dispose();
        }
      }
    });

    // Clear AssetVault and THREE.Cache to prevent ~100MB leak per 2D/3D switch.
    // Presigned URLs change each load, so THREE.Cache would grow unbounded otherwise.
    if (this.experience.assetVault) {
      this.experience.assetVault.destroy();
    }
    if (typeof THREE !== "undefined" && THREE.Cache) {
      THREE.Cache.clear();
    }

    // Clear selection highlights so we don't hold references to removed objects
    if (this.experience.selectionHighlightManager) {
      this.experience.selectionHighlightManager.clearForSceneDelete?.();
    }

    // Do not log to undo history when clearing for 2D/3D mode switch (reload follows and history is preserved)
    if (options?.reason !== "mode-switch") {
      this.eventHub.emit("logEvent", {
        eventType: "delete",
        data: sceneSnapshot,
      });
    }

    this.eventHub.emit("xmd");
  }
}

export default DeleteScene;
